import { Fragment, useState } from 'react';
import { useSnackbar } from 'notistack';
// next
import { useRouter } from 'next/router';
// mui
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import {
  NegotiationOfferCard,
  NegotiationRejectForm,
  NegotiationConditions,
  ExtendedNegoDeclineDialog,
  BeforeNegotiationCard,
  MessagePopover,
  MessageDialog,
} from './';
import Iconify from '../../components/Iconify';
// hooks
import useResponsive from '../../hooks/useResponsive';
// utils
import axios from '../../utils/axios';
import { fKrDateAbsoulte, fKrSendKST } from '../../utils/formatTime';

// ----------

export default function NegotiationCard({ offer, reloadOffer }) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { oid } = router.query;
  const smUp = useResponsive('up', 'sm');

  const [rejectDialog, setRejectDialog] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [openNegoDeclineDialog, setOpenNegoDeclineDialog] = useState(false);
  const [proceedNegotiation, setProceedNegotiation] = useState(false);

  const openRejectDialog = async () => {
    setRejectDialog(true);
  }

  const closeRejectDialog = async () => {
    setRejectDialog(false);
  }

  const reloadOfferAfterNego = async () => {
    await reloadOffer();
    setProceedNegotiation(false);
  }

  const declineNego = async () => {
    try {
      setIsSubmit(true);
      await axios.post(`api/crews/offers/${oid}/reject/`, {reason: "계약 되기까지 너무 오래 걸려요"});
      enqueueSnackbar('매칭을 취소하였습니다');
      reloadOffer();
    } catch (err) {
      enqueueSnackbar('매칭 취소에 실패하였습니다. 관리자에게 문의해주세요.', { variant: 'error' });
      console.error(err);
    } finally {
      setIsSubmit(false);
      setOpenNegoDeclineDialog(false);
    }
  }

  const cardHeader = (offer.offer_status === "crew-negotiate-to-company") ? "내가 제안한 계약 조건" : "계약 조건";
  const cardSubHeader = (offer.offer_status === "company-negotiate-rewrite") ? "회사에서 계약 조건을 수정해 줄 것을 요청했어요." : "";
  const beforeNego = offer.offer_status === "confirm-3way-meeting" || offer.offer_status === "waiting-offer-from-crew" || offer.offer_status === "company-negotiate-rewrite";
  const afterNego = offer.offer_status === 'crew-negotiate-to-company' || offer.offer_status === 'accept';

  return (
    <>
      {beforeNego && !proceedNegotiation && (
        <BeforeNegotiationCard
          offer={offer}
          openRejectDialog={openRejectDialog}
          reloadOffer={reloadOffer}
          proceed={() => setProceedNegotiation(true)}
        />
      )}
      {offer.offer_status === "crew-negotiate-to-company" && (
        <Stack sx={{ mb: 3 }} direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h4" gutterBottom>
              계약 조건을 제안했습니다.
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: "keep-all" }}>
              회사가 {!!offer.nego_deadline && (<b>{fKrSendKST(offer.nego_deadline)} 이내로</b>)} 계약 조건을 확인 후 계약을 수락, 거절 또는 조건 수정 요청을 할 예정입니다.
            </Typography>
            {offer.nego_extended && (
              <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-start"}}>
                <LoadingButton
                  type="button"
                  loading={isSubmit}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                    fontWeight: 'bold',
                  }}
                  onClick={() => setOpenNegoDeclineDialog(true)}
                  startIcon={<Iconify icon={'eva:alert-circle-fill'} />}
                >
                  너무 오래 걸려서 매칭을 취소하고 싶어요.
                </LoadingButton>
              </Box>
            )}
            {!smUp && (
              <Box sx={{ mt: 1.5 }}>
                <MessageDialog companyId={offer?.company_id} companyName={offer?.company_name} />
              </Box>
            )}
          </Stack>
          {smUp && (
          <Box>
            <MessagePopover companyId={offer?.company_id} companyName={offer?.company_name} />
          </Box>
          )}
        </Stack>
      )}
      {proceedNegotiation && (
        <Stack sx={{ mb: 3, ...smUp&&{ mt: !!offer?.instant_matched_at ? 0 : 5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
              사이드잡 계약을 진행하시려면{!smUp && <br />} 계약 조건을 작성해주세요.
            </Typography>
            {smUp && (
              <Box>
                <MessagePopover companyId={offer?.company_id} companyName={offer?.company_name} />
              </Box>
            )}
          </Stack>
          {!smUp && (
            <Box sx={{ mt: 2 }}>
              <MessageDialog companyId={offer?.company_id} companyName={offer?.company_name} />
            </Box>
          )}
        </Stack>
      )}
      {(!beforeNego || proceedNegotiation) && (
      <Card
        sx={{
          mb: 3,
          '&:hover': {
            boxShadow: (theme) => theme.customShadows.z24,
          },
        }}>
        <CardHeader
          title={cardHeader}
          titleTypographyProps={{ variant: "h5" }}
          subheader={cardSubHeader}
          subheaderTypographyProps={{ variant: smUp ? "body1" : "body2", color: "primary" }}
        />
        <CardContent>
          <Stack spacing={1}>
            {offer.offer_status === "accept" && (
              <Fragment>
                <Typography variant="body1" sx={{ color: 'text.secondary' }} paragraph>
                  계약 기간은 {fKrDateAbsoulte(offer.confirmed_start_date)} ~ {fKrDateAbsoulte(offer.confirmed_end_date)} 입니다.
                </Typography>
              </Fragment>
            )}

            {(!afterNego || proceedNegotiation) && (
              <NegotiationOfferCard offer={offer} openRejectDialog={openRejectDialog} reloadOffer={reloadOfferAfterNego} back={() => setProceedNegotiation(false)} />
            )}
            {afterNego && (
              <NegotiationConditions offer={offer} />
            )}
          </Stack>
        </CardContent>
      </Card>
      )}
      <ExtendedNegoDeclineDialog
        isOpen={openNegoDeclineDialog}
        closeDialog={() => setOpenNegoDeclineDialog(false)}
        isSubmit={isSubmit}
        declineNego={declineNego}
      />
      <NegotiationRejectForm
        rejectDialog={rejectDialog}
        closeRejectDialog={closeRejectDialog}
        reloadOffer={reloadOffer}
      />
    </>
  )
};
